import type { ReactNode } from 'react'

/**
 * Panelin bölüm bandı — sitenin cam bandının ta kendisi.
 *
 * Aynı çizim üç yerde birbirinden habersiz duruyordu: `SettingsSection`,
 * `Activity`'nin kendi `Band`'i ve `Dashboard`'un `SectionCanvas`'ı. Dördüncü
 * bir kopya yazmak yerine kabuk buraya alındı; bundan sonra bir bölüm eklerken
 * uydurulacak bir görünüm yok.
 *
 * `hint` başlığın altına, `action` sağına oturur ve başlık satırı sarmalıdır:
 * dar ekranda arama kutusu ve düğmeler alt satıra iner, başlık yerinde kalır.
 */
export default function Panel({
  title, hint, action, bodyClassName = 'p-4 md:p-5', className = '', children,
}: {
  /** Metin ya da metin + yanında bir sayı (`İnaktifler 12`); öğeler `gap-2.5` ile dizilir. */
  title?: ReactNode
  hint?: string
  action?: ReactNode
  /** Gövde dolgusunu iptal etmek için: tam kanama takvim ızgarası gibi. */
  bodyClassName?: string
  /**
   * Bandın kendisine eklenecek sınıflar. Tam kanama bir gövde `overflow-hidden`
   * ister — yoksa ızgaranın köşeleri yuvarlatılmış kenarın dışına taşar. Bu
   * varsayılan **değildir**: bandın içinde açılan emoji seçici gibi taşan
   * katmanlar kırpılırdı.
   */
  className?: string
  children: ReactNode
}) {
  return (
    <section className={`rounded-md border border-white/10 bg-black/25 backdrop-blur-sm ${className}`}>
      {(title || action || hint) && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-white/10 px-5 py-4">
          {title && (
            <h2 className="flex items-center gap-2.5 font-display text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/70">
              <span aria-hidden className="h-px w-6 shrink-0 bg-primary" />
              {title}
            </h2>
          )}
          {action && <div className="ml-auto flex shrink-0 flex-wrap items-center gap-1.5">{action}</div>}
          {hint && (
            <p className="w-full max-w-prose text-xs leading-relaxed text-muted-foreground">{hint}</p>
          )}
        </div>
      )}
      <div className={bodyClassName}>{children}</div>
    </section>
  )
}

/* ── Paylaşılan yüzey sınıfları ────────────────────────────────────────────── */

/** Bandın içindeki kart/satır dolgusu. Panelin her yerinde aynı. */
export const panelCard = 'rounded-sm border border-white/10 bg-white/[0.035]'

/** Tıklanabilir kart: kenarı aydınlanır, zemini değil — kızıl rasyonlu kalır. */
export const panelCardInteractive =
  `${panelCard} transition-colors hover:border-white/25 hover:bg-white/[0.06]`

export const panelInput =
  'w-full rounded-sm border border-white/10 bg-white/5 px-3 py-1.5 text-sm placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none'

/** Seçili duruma geçen filtre/sekme düğmesi. */
export function panelChip(active: boolean) {
  return `cursor-pointer rounded-sm border px-2.5 py-1 font-display text-[11px] font-bold uppercase tracking-[0.14em] transition-colors ${
    active
      ? 'border-primary/60 bg-primary/15 text-foreground'
      : 'border-white/10 bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-foreground'
  }`
}

/**
 * `panelChip`'in veri taşıyan hali: mevki adı gibi kullanıcı verisi büyütülmez
 * (`Infantry` → `İNFANTRY`), o yüzden büyük harfli tipografi burada yok.
 */
export function panelDataChip(active: boolean) {
  return `flex cursor-pointer items-center gap-1.5 rounded-sm border px-2 py-1 text-xs transition-colors ${
    active
      ? 'border-primary/60 bg-primary/15 text-foreground'
      : 'border-white/10 bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-foreground'
  }`
}

/** Bölümün birincil eylemi. Kızıl yalnızca burada yüzey olur. */
export const panelPrimaryButton =
  'flex h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-sm bg-primary/80 px-3 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-primary-foreground transition-colors hover:bg-primary disabled:opacity-40'

/**
 * Yalnızca simge taşıyan eylem — kare, 32px.
 *
 * ⚠️ Bu `panelGhostButton` + `px-0` **değildir**. Tailwind çıktısını kendi
 * kanonik sırasına göre dizer ve `.px-3` dosyada `.px-0`'dan sonra gelir, yani
 * sınıf adını sonraya yazmak dolguyu iptal etmez; simge 32px'lik kutuda 24px
 * dolgunun içinde kalırdı.
 */
export const panelIconButton =
  'flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-sm border border-white/10 bg-white/5 text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground disabled:opacity-40'

export const panelGhostButton =
  'flex h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-sm border border-white/10 bg-white/5 px-3 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground disabled:opacity-40'

/** Doldurulmayı bekleyen boşluk — kesikli, çünkü hata değil. */
export function PanelEmpty({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-sm border border-dashed border-white/10 py-8 text-center text-xs text-muted-foreground/70">
      {children}
    </p>
  )
}
